import React from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useSmartUploadEnabled } from '../../../../services/featureFlags.service';
import MainLayout from '../../../../components/MainLayout/MainLayout';
import { KycDocumentsPage } from './SmartUpload/KycDocumentsPage';
import './SmartUploadTab.scss';

/**
 * Smart Upload (OCR) tab in the Investor Dashboard.
 * Hosts the KycDocumentsPage component tree described in
 * KYC_DOCUMENT_PLAN.md §3.8.
 *
 * Journey context (family redesign): /investor/documents-center?journeyId=X scopes the
 * requirement view + uploads to that journey's PERSON. No param = the login investor's
 * own person — the standalone document manager, exactly as before.
 */
export const SmartUploadTab: React.FC = () => {
  const [searchParams] = useSearchParams();
  const journeyId = searchParams.get('journeyId');
  const freshMode = searchParams.get('fresh') === '1';
  // mode=upload: journey-less page still shows the uploader (login person's
  // requirements) — used by flows that need documents completed outside a journey,
  // e.g. the delegation KYC gate. Default journey-less = document manager only.
  const uploadMode = searchParams.get('mode') === 'upload';
  // Smart Upload switched off on the server: the upload window (journey view or mode=upload) must
  // never show — send investors to their dashboard and agents to the investor's profile. While the
  // switch is loading, render nothing. The journey-less page is only "My Documents" (list + View,
  // no OCR, no upload) and stays available either way (decision 2026-10-23).
  const smartUploadEnabled = useSmartUploadEnabled();
  const { investorId } = useParams<{ investorId?: string }>();
  const showsUploader = !!journeyId || uploadMode;
  if (showsUploader && smartUploadEnabled === null) {
    return null;
  }
  if (showsUploader && smartUploadEnabled === false) {
    return <Navigate replace to={investorId ? `/service-agent/investors/${investorId}/profile` : '/investor/dashboard'} />;
  }
  return (
    <div className="smart-upload-tab">
      <KycDocumentsPage journeyId={journeyId} freshMode={freshMode} uploadMode={uploadMode} />
    </div>
  );
};

/**
 * Investor Documents Center page frame. The OCR wording shows only while Smart Upload is switched
 * on; switched off (or still loading) the page is just "My Documents", so the subtitle says so
 * (decision 2026-10-23).
 */
export const DocumentsCenterLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const smartUploadEnabled = useSmartUploadEnabled();
  const subtitle = smartUploadEnabled === true
    ? 'Upload your KYC documents — we extract and validate key fields automatically via OCR'
    : 'View the KYC documents you have uploaded';
  return (
    <MainLayout title="Documents Center" subtitle={subtitle}>
      {children}
    </MainLayout>
  );
};

export default SmartUploadTab;
